import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
    HOME_PATH,
    HOSPITAL_USER_PATH,
    INDIVIDUAL_USER_PATH,
    LOGIN_PATH,
    NOTIFICATION_PATH,
    DEVICE_LIST_PATH,
    ANALYTICS_PATH,
    HELP_AND_SUPPORT_PATH,
    HOSPITAL_PATH,
    NURSE_PATH,
    MANAGEMENT_PATH,
    PATIENT_PATH,
    DEVICE_PATH,
    NOTIFICATION_INDIVIDUAL_PATH,
    ACTIVITY_LOG_PATH,
    DASHBOARD_PATH,
    SETTING_PATH,
    HOSPITAL_DASHBOARD_PATH,
    NURSE_USER_PATH,
    PROFILE_PATH,
    ASSIGN_PATH,
    ASSIGN_LIST_PATH,
    AUDIT_LOG_PATH,
    INDIVIDUAL_DEVICE_PATH,
} from '@constants/routes';
import { MdOutlineDashboard } from 'react-icons/md';
import { IoPersonOutline, IoListSharp } from 'react-icons/io5';
import { PiHospital } from 'react-icons/pi';
import { MdNotificationsNone } from 'react-icons/md';
import { RiLogoutCircleRLine } from 'react-icons/ri';
import { useTranslation } from 'react-i18next';
import { useAppSelector, useAppDispatch } from '@store/hooks';
import { IoSettingsOutline } from 'react-icons/io5';
import { TbBrandGoogleAnalytics } from 'react-icons/tb';
import { MdHelpOutline } from 'react-icons/md';
import { MdKeyboardArrowUp, MdKeyboardArrowDown } from 'react-icons/md';
import NotificationRedIcon from '@components/SvgComponents/NotificationRedIcon';
import { DRIPSET_LOGO } from '@constants/images';
import { ADMIN, HOSPITAL, INDIVIDUAL, NURSE } from '@constants/constants';
import { FaUserNurse } from 'react-icons/fa6';
import { CgProfile } from 'react-icons/cg';
import { getDataUsingTenantId, getDataUsingUserId } from '@utils/sharedUtils';
import { HOSPITALS_TABLE, USER_INFO_TABLE } from '@constants/dynamoDBTableName';
import { RxActivityLog } from 'react-icons/rx';
import {
    setHospitalBlocked,
    setLoggedInUserRole,
    setLoggedInUserStatus,
} from '@store/reducers/auth/loginSlice';
import { logout } from '@store/reducers/auth/authReducer';
import { AiOutlineAudit } from 'react-icons/ai';
import { fetchUserAttributes } from 'aws-amplify/auth';
import { clearLocalStoragePreserveLanguage } from '@utils/utils';

interface SidebarProps {
    onLogoutClick: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ onLogoutClick }) => {
    const [activeLink, setActiveLink] = useState(HOME_PATH);
    const [activeSubMenuLink, setActiveSubMenuLink] = useState('');
    const [isManagementOpen, setIsManagementOpen] = useState(false);
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const userRole = useAppSelector((state) => state.login.loggedInUserRole);
    const loggedInUserTenantID = useAppSelector(
        (state) => state.login.loggedInUserTenantID
    );
    const loggedInUserCognitoUuid = useAppSelector(
        (state) => state.login.loggedInUserCognitoUuid
    );

    async function currentAuthenticatedUser() {
        try {
            const attributes = await fetchUserAttributes();
        } catch (err) {
            // console.log(err);
            clearLocalStoragePreserveLanguage();
            window.location.href = LOGIN_PATH;
        }
    }

    useEffect(() => {
        const pathname = location.pathname;
        // Check if the pathname matches any of the paths from AppConfig
        if (
            pathname === INDIVIDUAL_USER_PATH ||
            pathname.startsWith(INDIVIDUAL_USER_PATH + '/')
        ) {
            setActiveLink(INDIVIDUAL_USER_PATH);
        } else if (
            pathname === HOSPITAL_USER_PATH ||
            pathname.startsWith(HOSPITAL_USER_PATH + '/')
        ) {
            setActiveLink(HOSPITAL_USER_PATH);
        } else if (
            pathname === NURSE_USER_PATH ||
            pathname.startsWith(NURSE_USER_PATH + '/')
        ) {
            setActiveLink(NURSE_USER_PATH);
        } else if (pathname === AUDIT_LOG_PATH) {
            setActiveLink(AUDIT_LOG_PATH);
        } else if (
            pathname === NOTIFICATION_PATH ||
            pathname.startsWith(NOTIFICATION_PATH + '/')
        ) {
            setActiveLink(NOTIFICATION_PATH);
        } else if (
            pathname === INDIVIDUAL_DEVICE_PATH ||
            pathname.startsWith(INDIVIDUAL_DEVICE_PATH + '/')
        ) {
            setActiveLink(INDIVIDUAL_DEVICE_PATH);
        } else if (
            pathname === NOTIFICATION_INDIVIDUAL_PATH ||
            pathname.startsWith(NOTIFICATION_INDIVIDUAL_PATH + '/')
        ) {
            setActiveLink(NOTIFICATION_INDIVIDUAL_PATH);
        } else if (
            pathname === ACTIVITY_LOG_PATH ||
            pathname.startsWith(ACTIVITY_LOG_PATH + '/')
        ) {
            setActiveLink(ACTIVITY_LOG_PATH);
        } else if (
            pathname === MANAGEMENT_PATH ||
            pathname.startsWith(MANAGEMENT_PATH + '/')
        ) {
            setActiveLink(MANAGEMENT_PATH);
        } else if (
            pathname === DEVICE_LIST_PATH ||
            pathname.startsWith(DEVICE_LIST_PATH + '/')
        ) {
            setActiveLink(DEVICE_LIST_PATH);
        } else if (
            pathname === ANALYTICS_PATH ||
            pathname.startsWith(ANALYTICS_PATH + '/')
        ) {
            setActiveLink(ANALYTICS_PATH);
        } else if (
            pathname === DASHBOARD_PATH ||
            pathname.startsWith(ANALYTICS_PATH + '/')
        ) {
            setActiveLink(DASHBOARD_PATH);
        } else if (
            pathname === SETTING_PATH ||
            pathname.startsWith(SETTING_PATH + '/')
        ) {
            setActiveLink(SETTING_PATH);
        } else if (
            pathname === PROFILE_PATH ||
            pathname.startsWith(PROFILE_PATH + '/')
        ) {
            setActiveLink(PROFILE_PATH);
        } else if (pathname === HOSPITAL_DASHBOARD_PATH) {
            setActiveLink(HOSPITAL_DASHBOARD_PATH);
        } else if (pathname === HELP_AND_SUPPORT_PATH) {
            setActiveLink(HELP_AND_SUPPORT_PATH);
        } else {
            setActiveLink(HOME_PATH);
        }
    }, [location.pathname]);

    const handleLinkClick = async (path: any) => {
        currentAuthenticatedUser();
        let userInfo;

        if (userRole.toLowerCase() == HOSPITAL.toLowerCase() || userRole.toLowerCase() == NURSE.toLowerCase()) {
            const hospitalData = await getDataUsingTenantId(
                HOSPITALS_TABLE,
                loggedInUserTenantID
            );

            if (
                hospitalData &&
                hospitalData.Items &&
                hospitalData.Items?.length != 0
            ) {
                const res = hospitalData.Items[0];
                const status = res.status?.S;
                if (
                    status &&
                    status.toLowerCase() != 'approved' &&
                    status.toLowerCase() != 'verified'
                ) {
                    clearLocalStoragePreserveLanguage();
                    dispatch(logout());
                    dispatch(setHospitalBlocked(true));
                    dispatch(setLoggedInUserRole(userRole));
                    dispatch(setLoggedInUserStatus(status));
                    navigate(HOME_PATH);
                    return;
                }
            }
        } else {
            userInfo = await getDataUsingUserId(
                USER_INFO_TABLE,
                loggedInUserCognitoUuid
            );

            if (userInfo && userInfo.Items && userInfo.Items?.length != 0) {
                const res = userInfo.Items[0];
                const status = res.status?.S;
                if (
                    status &&
                    status.toLowerCase() != 'approved' &&
                    status.toLowerCase() != 'verified'
                ) {
                    clearLocalStoragePreserveLanguage();
                    dispatch(logout());
                    dispatch(setLoggedInUserRole(userRole));
                    dispatch(setLoggedInUserStatus(status));
                    navigate(HOME_PATH);
                    return;
                }
            }
        }

        setActiveLink(path);
        if (path != MANAGEMENT_PATH) {
            setActiveSubMenuLink('');
            setIsManagementOpen(false);
        }
    };
    const handleSubmenuLinkClick = (path: any) => {
        setActiveSubMenuLink(path);
    };
    const toggleManagementMenu = () => {
        setIsManagementOpen(!isManagementOpen);
    };

    return (
        <div
            className="flex flex-col bg-white w-60"
            style={{
                boxShadow: '2px 0 16px rgba(0,0,0,0.06)',
                borderRight: '1px solid #F1F5F9',
                height: '100vh',
                overflowY: 'auto',
                flexShrink: 0,
            }}
        >
            <div className="">
                <div className="flex items-center justify-center h-16">
                    <img src={DRIPSET_LOGO} className="h-12" alt="Logo" />
                </div>
                {userRole.toLowerCase() == ADMIN.toLowerCase() && (
                    <ul className="mt-4" style={{ listStyle: 'none', padding: '0 10px', margin: 0 }}>
                        <li
                            className={`px-4 font-bold cursor-pointer mb-3 border-l-4 ${activeLink === DASHBOARD_PATH
                                    ? '!border-primary inline-block'
                                    : 'border-transparent'
                                }`}
                            onClick={() => handleLinkClick(DASHBOARD_PATH)}
                        >
                            <Link to={DASHBOARD_PATH} style={{ textDecoration: 'none' }}>
                                <div
                                    className={`flex items-center pl-[1rem] py-2 ${activeLink === DASHBOARD_PATH
                                            ? '!bg-primary text-white rounded w-48 '
                                            : ''
                                        }`}
                                >
                                    <MdOutlineDashboard style={{ marginRight: 10, fontSize: 19, flexShrink: 0 }} />
                                    {t('sidebar.dashboard')}
                                </div>
                            </Link>
                        </li>
                        <li
                            className={`px-4 font-bold cursor-pointer mb-3 border-l-4 ${activeLink === INDIVIDUAL_USER_PATH
                                    ? '!border-primary inline-block'
                                    : 'border-transparent'
                                }`}
                            onClick={() =>
                                handleLinkClick(INDIVIDUAL_USER_PATH)
                            }
                        >
                            <Link to={INDIVIDUAL_USER_PATH}>
                                <div
                                    className={`flex items-center pl-[1rem] py-2 ${activeLink === INDIVIDUAL_USER_PATH
                                            ? '!bg-primary text-white rounded w-48 '
                                            : ''
                                        }`}
                                >
                                    <IoPersonOutline className="mr-2 text-xl" />
                                    {t('sidebar.individual')}
                                </div>
                            </Link>
                        </li>
                        <li
                            className={`px-4 font-bold cursor-pointer mb-3 border-l-4 ${activeLink === HOSPITAL_USER_PATH
                                    ? '!border-primary inline-block'
                                    : 'border-transparent'
                                }`}
                            onClick={() => handleLinkClick(HOSPITAL_USER_PATH)}
                        >
                            <Link to={HOSPITAL_USER_PATH}>
                                <div
                                    className={`flex items-center pl-[1rem] py-2 ${activeLink === HOSPITAL_USER_PATH
                                            ? '!bg-primary text-white rounded w-48 '
                                            : ''
                                        }`}
                                >
                                    <PiHospital className="mr-2 text-xl" />
                                    {t('sidebar.hospital')}
                                </div>
                            </Link>
                        </li>
                        <li
                            className={`px-4 font-bold cursor-pointer mb-3 border-l-4 ${activeLink === NURSE_USER_PATH
                                    ? '!border-primary inline-block'
                                    : 'border-transparent'
                                }`}
                            onClick={() => handleLinkClick(NURSE_USER_PATH)}
                        >
                            <Link to={NURSE_USER_PATH}>
                                <div
                                    className={`flex items-center pl-[1rem] py-2 ${activeLink === NURSE_USER_PATH
                                            ? '!bg-primary text-white rounded w-48 '
                                            : ''
                                        }`}
                                >
                                    <FaUserNurse className="mr-2 text-xl" />
                                    {t('sidebar.nurse')}
                                </div>
                            </Link>
                        </li>
                        <li
                            className={`px-4 font-bold cursor-pointer mb-3 border-l-4 ${activeLink === ACTIVITY_LOG_PATH
                                    ? '!border-primary inline-block'
                                    : 'border-transparent'
                                }`}
                            onClick={() => handleLinkClick(ACTIVITY_LOG_PATH)}
                        >
                            <Link to={ACTIVITY_LOG_PATH}>
                                <div
                                    className={`flex items-center pl-[1rem] py-2 ${activeLink === ACTIVITY_LOG_PATH
                                            ? '!bg-primary text-white rounded w-48 '
                                            : ''
                                        }`}
                                >
                                    <RxActivityLog className="mr-2 text-xl" />
                                    {t('sidebar.activity_log')}
                                </div>
                            </Link>
                        </li>
                        <li
                            className={`px-4 font-bold cursor-pointer mb-3 border-l-4 ${activeLink === AUDIT_LOG_PATH
                                    ? '!border-primary inline-block'
                                    : 'border-transparent'
                                }`}
                            onClick={() => handleLinkClick(AUDIT_LOG_PATH)}
                        >
                            <Link to={AUDIT_LOG_PATH}>
                                <div
                                    className={`flex items-center pl-[1rem] py-2 ${activeLink === AUDIT_LOG_PATH
                                            ? '!bg-primary text-white rounded w-48 '
                                            : ''
                                        }`}
                                >
                                    <AiOutlineAudit className="mr-2 text-xl" />
                                    {t('sidebar.audit_log')}
                                </div>
                            </Link>
                        </li>
                        <li
                            className={`px-4 font-bold cursor-pointer mb-3 border-l-4 ${activeLink === NOTIFICATION_PATH
                                    ? '!border-primary inline-block'
                                    : 'border-transparent'
                                }`}
                            onClick={() => handleLinkClick(NOTIFICATION_PATH)}
                        >
                            <Link to={NOTIFICATION_PATH}>
                                <div
                                    className={`flex items-center pl-[1rem] py-2 ${activeLink === NOTIFICATION_PATH
                                            ? '!bg-primary text-white rounded w-48 '
                                            : ''
                                        }`}
                                >
                                    <MdNotificationsNone className="mr-2 text-xl" />
                                    {t('sidebar.notification')}
                                </div>
                            </Link>
                        </li>
                    </ul>
                )}

                {(userRole.toLowerCase() == INDIVIDUAL.toLowerCase() ||
                    userRole.toLowerCase() == NURSE.toLowerCase()) && (
                        <ul className="mt-4">
                            <li
                                className={`px-4 font-bold cursor-pointer mb-3 border-l-4 ${activeLink === DEVICE_LIST_PATH
                                        ? '!border-primary inline-block'
                                        : 'border-transparent'
                                    }`}
                                onClick={() => handleLinkClick(DEVICE_LIST_PATH)}
                            >
                                <Link to={DEVICE_LIST_PATH}>
                                    <div
                                        className={`flex items-center pl-[1rem] py-2 ${activeLink === DEVICE_LIST_PATH
                                                ? '!bg-primary text-white rounded w-48 '
                                                : ''
                                            }`}
                                    >
                                        <IoListSharp className="mr-2 text-xl" />

                                        {t('sidebar.device_list')}
                                    </div>
                                </Link>
                            </li>
                            {userRole.toLowerCase() == INDIVIDUAL.toLowerCase() && (
                                <li
                                    className={`menu-item py-2 px-4 font-bold cursor-pointer ${activeSubMenuLink === INDIVIDUAL_DEVICE_PATH
                                            ? '!text-primary'
                                            : ''
                                        }`}
                                    onClick={() =>
                                        handleSubmenuLinkClick(
                                            INDIVIDUAL_DEVICE_PATH
                                        )
                                    }
                                >
                                    <Link to={INDIVIDUAL_DEVICE_PATH}>
                                        <div
                                            className={`flex items-center pl-[1rem] py-2 ${activeLink ===
                                                    INDIVIDUAL_DEVICE_PATH
                                                    ? '!bg-primary text-white rounded w-48 '
                                                    : ''
                                                }`}
                                        >
                                            <IoListSharp className="mr-2 text-xl" />
                                            {t('sidebar.device')}
                                        </div>
                                    </Link>
                                </li>
                            )}
                            <li
                                className={`px-4 font-bold cursor-pointer mb-3 border-l-4 ${activeLink === ANALYTICS_PATH
                                        ? '!border-primary inline-block'
                                        : 'border-transparent'
                                    }`}
                                onClick={() => handleLinkClick(ANALYTICS_PATH)}
                            >
                                <Link to={ANALYTICS_PATH}>
                                    <div
                                        className={`flex items-center pl-[1rem] py-2 ${activeLink === ANALYTICS_PATH
                                                ? '!bg-primary text-white rounded w-48 '
                                                : ''
                                            }`}
                                    >
                                        <TbBrandGoogleAnalytics className="mr-2 text-xl" />
                                        {t('sidebar.analytics')}
                                    </div>
                                </Link>
                            </li>
                            {userRole.toLowerCase() == INDIVIDUAL.toLowerCase() && (
                                <li
                                    className={`px-4 font-bold cursor-pointer mb-3 border-l-4 ${activeLink === ACTIVITY_LOG_PATH
                                            ? '!border-primary inline-block'
                                            : 'border-transparent'
                                        }`}
                                    onClick={() =>
                                        handleLinkClick(ACTIVITY_LOG_PATH)
                                    }
                                >
                                    <Link to={ACTIVITY_LOG_PATH}>
                                        <div
                                            className={`flex items-center pl-[1rem] py-2 ${activeLink === ACTIVITY_LOG_PATH
                                                    ? '!bg-primary text-white rounded w-48 '
                                                    : ''
                                                }`}
                                        >
                                            <RxActivityLog className="mr-2 text-xl" />
                                            {t('sidebar.activity_log')}
                                        </div>
                                    </Link>
                                </li>
                            )}
                            <li
                                className={`px-4 font-bold cursor-pointer mb-3 border-l-4 ${activeLink === NOTIFICATION_INDIVIDUAL_PATH
                                        ? '!border-primary inline-block'
                                        : 'border-transparent'
                                    }`}
                                onClick={() =>
                                    handleLinkClick(NOTIFICATION_INDIVIDUAL_PATH)
                                }
                            >
                                <Link to={NOTIFICATION_INDIVIDUAL_PATH}>
                                    <div
                                        className={`flex items-center pl-[1rem] py-2 relative ${activeLink ===
                                                NOTIFICATION_INDIVIDUAL_PATH
                                                ? '!bg-primary text-white rounded w-48 '
                                                : ''
                                            }`}
                                    >
                                        <MdNotificationsNone className="mr-2 text-xl" />
                                        {t('sidebar.notification')}
                                    </div>
                                </Link>
                            </li>
                        </ul>
                    )}
                {userRole.toLowerCase() == HOSPITAL.toLowerCase() && (
                    <ul className="mt-4">
                        <li
                            className={`px-4 font-bold cursor-pointer mb-3 border-l-4 ${activeLink === HOSPITAL_DASHBOARD_PATH
                                    ? '!border-primary inline-block'
                                    : 'border-transparent'
                                }`}
                            onClick={() =>
                                handleLinkClick(HOSPITAL_DASHBOARD_PATH)
                            }
                        >
                            <Link to={HOSPITAL_DASHBOARD_PATH}>
                                <div
                                    className={`flex items-center pl-[1rem] py-2 ${activeLink === HOSPITAL_DASHBOARD_PATH
                                            ? '!bg-primary text-white rounded w-48 '
                                            : ''
                                        }`}
                                >
                                    <MdOutlineDashboard className="mr-2 text-xl" />

                                    {t('sidebar.dashboard')}
                                </div>
                            </Link>
                        </li>
                        <li
                            className={`main-menu px-4 font-bold cursor-pointer border-l-4 ${activeLink === MANAGEMENT_PATH
                                    ? '!border-primary inline-block'
                                    : 'border-transparent'
                                }`}
                            onClick={() => handleLinkClick(MANAGEMENT_PATH)}
                        >
                            <div
                                className={`flex items-center pl-[1rem] py-2  ${activeLink === MANAGEMENT_PATH
                                        ? '!bg-primary text-white rounded w-48 '
                                        : ''
                                    }`}
                                onClick={toggleManagementMenu}
                            >
                                <PiHospital className="mr-2 text-xl" />
                                <span className="mr-1">
                                    {t('sidebar.management')}
                                </span>

                                {isManagementOpen ? (
                                    <MdKeyboardArrowUp className="text-xl" />
                                ) : (
                                    <MdKeyboardArrowDown className="text-xl" />
                                )}
                            </div>
                        </li>
                        {isManagementOpen && (
                            <ul className="mt-2 ml-[0.5rem]">
                                <li
                                    className={`menu-item px-4 py-2 font-bold cursor-pointer ${activeSubMenuLink === HOSPITAL_PATH
                                            ? '!text-primary'
                                            : ''
                                        }`}
                                    onClick={() =>
                                        handleSubmenuLinkClick(HOSPITAL_PATH)
                                    }
                                >
                                    <Link to={HOSPITAL_PATH}>
                                        <div className="flex items-center pl-[1rem] ">
                                            {t('sidebar.hospital_info')}
                                        </div>
                                    </Link>
                                </li>
                                <li
                                    className={`menu-item py-2 px-4 font-bold cursor-pointer ${activeSubMenuLink === DEVICE_PATH
                                            ? '!text-primary'
                                            : ''
                                        }`}
                                    onClick={() =>
                                        handleSubmenuLinkClick(DEVICE_PATH)
                                    }
                                >
                                    <Link to={DEVICE_PATH}>
                                        <div className="flex items-center pl-[1rem] ">
                                            {t('sidebar.device')}
                                        </div>
                                    </Link>
                                </li>
                                <li
                                    className={`menu-item px-4 py-2 font-bold cursor-pointer ${activeSubMenuLink === NURSE_PATH
                                            ? '!text-primary'
                                            : ''
                                        }`}
                                    onClick={() =>
                                        handleSubmenuLinkClick(NURSE_PATH)
                                    }
                                >
                                    <Link to={NURSE_PATH}>
                                        <div className="flex items-center pl-[1rem] ">
                                            {t('sidebar.nurse')}
                                        </div>
                                    </Link>
                                </li>
                                <li
                                    className={`menu-item px-4 py-2 font-bold cursor-pointer ${activeSubMenuLink === ASSIGN_LIST_PATH
                                            ? '!text-primary'
                                            : ''
                                        }`}
                                    onClick={() =>
                                        handleSubmenuLinkClick(ASSIGN_LIST_PATH)
                                    }
                                >
                                    <Link to={ASSIGN_LIST_PATH}>
                                        <div className="flex items-center pl-[1rem] ">
                                            {t('sidebar.assign')}
                                        </div>
                                    </Link>
                                </li>
                            </ul>
                        )}

                        <li
                            className={`px-4 font-bold cursor-pointer my-4 border-l-4 ${activeLink === DEVICE_LIST_PATH
                                    ? '!border-primary inline-block'
                                    : 'border-transparent'
                                }`}
                            onClick={() => handleLinkClick(DEVICE_LIST_PATH)}
                        >
                            <Link to={DEVICE_LIST_PATH}>
                                <div
                                    className={`flex items-center pl-[1rem] py-2  ${activeLink === DEVICE_LIST_PATH
                                            ? '!bg-primary text-white rounded w-48 '
                                            : ''
                                        }`}
                                >
                                    <IoListSharp className="mr-2 text-xl" />

                                    {t('sidebar.device_list')}
                                </div>
                            </Link>
                        </li>
                        <li
                            className={`px-4 font-bold cursor-pointer mb-3 border-l-4 ${activeLink === ACTIVITY_LOG_PATH
                                    ? '!border-primary inline-block'
                                    : 'border-transparent'
                                }`}
                            onClick={() => handleLinkClick(ACTIVITY_LOG_PATH)}
                        >
                            <Link to={ACTIVITY_LOG_PATH}>
                                <div
                                    className={`flex items-center pl-[1rem] py-2 ${activeLink === ACTIVITY_LOG_PATH
                                            ? '!bg-primary text-white rounded w-48 '
                                            : ''
                                        }`}
                                >
                                    <RxActivityLog className="mr-2 text-xl" />
                                    {t('sidebar.activity_log')}
                                </div>
                            </Link>
                        </li>
                        <li
                            className={`px-4 font-bold cursor-pointer mb-3 border-l-4 ${activeLink === NOTIFICATION_INDIVIDUAL_PATH
                                    ? '!border-primary inline-block'
                                    : 'border-transparent'
                                }`}
                            onClick={() =>
                                handleLinkClick(NOTIFICATION_INDIVIDUAL_PATH)
                            }
                        >
                            <Link to={NOTIFICATION_INDIVIDUAL_PATH}>
                                <div
                                    className={`flex items-center pl-[1rem] py-2 ${activeLink ===
                                            NOTIFICATION_INDIVIDUAL_PATH
                                            ? '!bg-primary text-white rounded w-48 '
                                            : ''
                                        }`}
                                >
                                    <MdNotificationsNone className="mr-2 text-xl" />
                                    {t('sidebar.notification')}
                                </div>
                            </Link>
                        </li>
                    </ul>
                )}
            </div>

            {/* Monitor with Confidence Card */}
            <div className="mx-3 my-4 rounded-2xl overflow-hidden" style={{ background: 'linear-gradient(135deg, #0f2d8a 0%, #1a4fd8 35%, #2563eb 65%, #38bdf8 100%)' }}>
                <div className="p-4 pb-2">
                    <p className="text-white font-extrabold text-[12px] leading-snug mb-3 text-center">Monitor with Confidence</p>
                    <p className="leading-snug mb-2 text-center text-white" style={{ fontSize: '12px' }}>
                        Track all infusion devices <br />
                        in real-time and <br />
                        ensure patient safety.
                    </p>
                    {/* Infusion Bag Image */}
                    <div className="flex justify-center items-center pt-2 pb-3">
                        <img
                            src="/public/images/infusion-bag-icon.png"
                            alt="Infusion Bag"
                            className="h-24 w-auto object-contain"
                            style={{ filter: 'brightness(0) invert(1)' }}
                        />
                    </div>
                </div>
            </div>

            <div className="flex flex-col mt-auto">
                <div className="">
                    <button
                        className={`px-4 font-bold cursor-pointer mb-3 border-l-4 ${activeLink === PROFILE_PATH
                                ? '!border-primary inline-block'
                                : 'border-transparent'
                            }`}
                        onClick={() => handleLinkClick(PROFILE_PATH)}
                    >
                        <Link to={PROFILE_PATH}>
                            <div
                                className={`flex items-center pl-[1rem] py-2 ${activeLink === PROFILE_PATH
                                        ? '!bg-primary text-white rounded w-48 '
                                        : ''
                                    }`}
                            >
                                <CgProfile className="mr-2 text-xl" />
                                {t('sidebar.profile')}
                            </div>
                        </Link>
                    </button>
                </div>
                <div className="">
                    <button
                        className={`px-4 font-bold cursor-pointer mb-3 border-l-4 ${activeLink === SETTING_PATH
                                ? '!border-primary inline-block'
                                : 'border-transparent'
                            }`}
                        onClick={() => handleLinkClick(SETTING_PATH)}
                    >
                        <Link to={SETTING_PATH}>
                            <div
                                className={`flex items-center pl-[1rem] py-2 ${activeLink === SETTING_PATH
                                        ? '!bg-primary text-white rounded w-48 '
                                        : ''
                                    }`}
                            >
                                <IoSettingsOutline className="mr-2 text-xl" />
                                {t('sidebar.setting')}
                            </div>
                        </Link>
                    </button>
                </div>
                {(userRole.toLowerCase() == INDIVIDUAL.toLowerCase() ||
                    userRole.toLowerCase() == NURSE.toLowerCase()) && (
                        <div>
                            <button
                                className={`px-4 font-bold cursor-pointer mb-3 border-l-4 ${activeLink === HELP_AND_SUPPORT_PATH
                                        ? '!border-primary inline-block'
                                        : 'border-transparent'
                                    }`}
                                onClick={() =>
                                    handleLinkClick(HELP_AND_SUPPORT_PATH)
                                }
                            >
                                <Link to={HELP_AND_SUPPORT_PATH}>
                                    <div
                                        className={`flex items-center pl-[1rem] py-2 ${activeLink === HELP_AND_SUPPORT_PATH
                                                ? '!bg-primary text-white rounded w-48 '
                                                : ''
                                            }`}
                                    >
                                        <MdHelpOutline className="mr-2 text-xl" />
                                        {t('sidebar.help_and_support')}
                                    </div>
                                </Link>
                            </button>
                        </div>
                    )}
                {userRole.toLowerCase() == HOSPITAL.toLowerCase() && (
                    <div>
                        <button
                            className={`px-4 font-bold cursor-pointer mb-3 border-l-4 ${activeLink === HELP_AND_SUPPORT_PATH
                                    ? '!border-primary inline-block'
                                    : 'border-transparent'
                                }`}
                            onClick={() =>
                                handleLinkClick(HELP_AND_SUPPORT_PATH)
                            }
                        >
                            <Link to={HELP_AND_SUPPORT_PATH}>
                                <div
                                    className={`flex items-center pl-[1rem] py-2 ${activeLink === HELP_AND_SUPPORT_PATH
                                            ? '!bg-primary text-white rounded w-48 '
                                            : ''
                                        }`}
                                >
                                    <MdHelpOutline className="mr-2 text-xl" />
                                    {t('sidebar.help_and_support')}
                                </div>
                            </Link>
                        </button>
                    </div>
                )}

                <div className="">
                    <button
                        className="px-4 font-bold cursor-pointer mb-3 border-l-4 border-transparent"
                        onClick={onLogoutClick}
                    >
                        <div className="flex items-center pl-[1rem] py-2">
                            <RiLogoutCircleRLine className="mr-2 text-xl" />
                            {t('sidebar.logout')}
                        </div>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default Sidebar;
